/**
 * Wait for a window variable to be defined before proceeding with function.
 * - Supports top-level variables (window.Shopify)
 * - Supports nested object variables (window.Shopify.theme)
 * @example
 * ```js
 * waitFor('Shopify')
 * .then(() => {
 *   // Waits until `window.Shopify` is defined before proceeding.
 * })
 * ```
 * @param {String} namespace - Named window variable to wait for.
 * @return {Object}
 */

const waitFor = (namespace) => {
  /**
   * Wait for a maximum of 5 seconds at 0.05s intervals
   */
  let attempts = 0;

  const getObject = (resolve, reject) =>
    setTimeout(() => {
      if (attempts === 100) reject(`Error: window.${namespace} timed out`);

      attempts++;

      const ready = Object.hasOwn(window, namespace);

      return !ready ? getObject(resolve) : resolve(window[namespace]);
    }, 50);

  return new Promise((resolve, reject) => {
    getObject(resolve, reject);
  });
};

const debounced = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const searchSelectors = {
  expandElement: '.js-element-expand',
  field: '.js-field',
  suggestions: '.js-suggestion',
  resultsHolder: '.js-results',
  close: '.js-search-close',
  noSearch: '.js-nosearch',
  resetButton: '.js-reset-search',
};

const searchClasses = {
  hidden: 'hidden',
  bodyClass: 'search-is-active',
  active: 'active',
  empty: 'is-empty',
  loading: 'loading',
};

const searchAttributes = {
  termText: 'data-term-condition-text',
};

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.expandElement = document.querySelectorAll(searchSelectors.expandElement);
    this.field = this.querySelector(searchSelectors.field);
    this.suggestions = this.querySelectorAll(searchSelectors.suggestions);
    this.results = this.querySelector(searchSelectors.resultsHolder);
    this.closeElements = this.querySelectorAll(searchSelectors.close);
    this.noSearchContent = this.querySelector(searchSelectors.noSearch);
    this.resetButton = this.querySelector(searchSelectors.resetButton);

    waitFor('Shopify')
      .then(this.init.bind(this))
      .catch((err) => window.console.error(err));
  }

  init() {
    this.search();
    this.chooseSuggestion();
    this.searchClose();
    this.eventsHandler();
  }

  eventsHandler() {
    this.expandElement.forEach(element => {
      element.addEventListener('click', () => {
        this.classList.add(searchClasses.active);
        document.body.classList.add(searchClasses.bodyClass);
        setTimeout(() => {
          this.field.focus();
        }, 100)
      });
    });

    document.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape') {
        return;
      }

      this.close();
    });

    this.resetButton.addEventListener('click', this.clickResetButton.bind(this));
  }

  chooseSuggestion() {
    this.suggestions.forEach(suggestion => {
      suggestion.addEventListener('click', () => {
        this.field.value = suggestion.innerText;

        this.field.dispatchEvent(new Event('input'));
      });
    });
  }

  search() {
    this.field.addEventListener(
      'input',
        debounced(() => {
          const searchTerm = this.field.value.trim();

          this.toggleResetButton();

          if (searchTerm.length >= 3) {
            this.classList.add(searchClasses.loading);
            this.getSearchResults(searchTerm);
            this.noSearchContent.classList.add(searchClasses.hidden);
          } else {
            this.classList.remove(searchClasses.loading);
            this.noSearchContent.classList.remove(searchClasses.hidden);
            this.results.innerHTML = `<p>${this.results.getAttribute(searchAttributes.termText)}</p>`;
          }

          if (searchTerm === '') {
            this.classList.remove(searchClasses.loading);
            this.results.classList.add(searchClasses.empty);
            this.results.innerHTML = '';
          }
        }, 300)
        .bind(this)
    );
  }

  getSearchResults(searchTerms) {
    fetch(
      `${window.routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerms
      )}&resources[limit_scope]=each&resources[options][fields]=author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor&section_id=predictive-search-results`
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const markup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(searchSelectors.resultsHolder).innerHTML;

        this.results.classList.remove(searchClasses.empty);
        this.results.innerHTML = markup;

        this.classList.remove(searchClasses.loading);
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        window.initializeCarouselSlider();
      });
  }

  searchClose() {
    this.closeElements.forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();

        this.close();
      });
    })
  }

  close() {
    this.resetSearch();

    this.classList.remove(searchClasses.active);
    document.body.classList.remove(searchClasses.bodyClass);
  }

  toggleResetButton() {
    const resetIsVisible = this.resetButton.classList.contains(searchClasses.active);

    if (this.field.value.length === 0 && resetIsVisible) {
      this.resetButton.classList.remove(searchClasses.active);
      this.resetSearch();
    } else if (this.field.value.length > 0 && !resetIsVisible) {
      this.resetButton.classList.add(searchClasses.active);
    }
  }

  resetSearch() {
    this.field.value = '';
    this.results.classList.add(searchClasses.empty);
    this.results.innerHTML = '';
  }

  clickResetButton() {
    this.resetSearch();
    this.toggleResetButton();
    this.noSearchContent.classList.remove(searchClasses.hidden);
  }
}

customElements.get('predictive-search') || customElements.define('predictive-search', PredictiveSearch);
